"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * "Life happens" section (item 16): a small, emotionally resonant beat
 * about Amy's plan-B recalculation, linking to /guides/save-your-day (a
 * guide page owned by a different work item — this only links to it, it
 * does not build it). No fabricated outcome numbers here, just the
 * mechanism: tell Amy what changed, she recalculates around it.
 */

import { Link } from "react-router-dom"
import { Sparkles } from "lucide-react"
import { useI18n } from "@/i18n"
import { Reveal } from "./Reveal"

export function SaveTheDaySection() {
  const { t } = useI18n()

  return (
    <section className="border-b border-line bg-paper">
      <div className="mx-auto max-w-6xl px-4 py-16 md:px-8">
        <Reveal className="relative overflow-hidden rounded-bb-xl border border-line bg-cream p-8 sm:p-12">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-16 -right-16 size-56 rounded-full bg-coral/15 blur-2xl"
          />
          <div className="relative flex flex-col items-start gap-4 md:max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-bb-full border border-coral/30 bg-coral/10 px-3 py-1 text-xs font-bold tracking-wide text-coral uppercase">
              <Sparkles aria-hidden="true" className="size-3.5" />
              {t("landingPage.saveTheDay.eyebrow")}
            </span>
            <h2 className="text-3xl font-bold text-ink md:text-4xl">
              {t("landingPage.saveTheDay.heading")}
            </h2>
            <p className="text-ink-2">{t("landingPage.saveTheDay.body")}</p>
            <Link
              to="/guides/save-your-day"
              className="min-h-11 inline-flex items-center gap-1.5 rounded-bb-full border border-ink/15 bg-paper px-5 text-sm font-semibold text-ink transition-transform hover:-translate-y-0.5"
            >
              {t("landingPage.saveTheDay.cta")}
              <span aria-hidden="true">→</span>
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  )
}
